import { JSONSchema7WithErrors } from '@cibo/core'
import {
  FormikConfig,
  FormikErrors,
  FormikProps,
  FormikProvider,
  FormikValues,
  useFormik,
} from 'formik'
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { jsonSchemaFormikValidator } from './jsonSchemaFormikValidator'

type FormikWithWarnings<Values extends FormikValues> = ReturnType<typeof useFormik<Values>> & {
  warnings: FormikErrors<Values>
  setWarnings: (warnings: FormikErrors<Values>) => void
}

export function useFormikWithWarnings<Values extends FormikValues>(
  config: FormikConfig<Values>
): FormikWithWarnings<Values> {
  const formik = useFormik(config)
  const [warnings, setWarnings] = useState<FormikErrors<Values>>({})

  return { ...formik, warnings, setWarnings }
}

function usePreviousValue<T>(value: T) {
  const ref = useRef<T>(value)
  useEffect(() => {
    ref.current = value
  }, [value])
  return ref.current
}

export function FormikWithWarningsProvider<Values extends FormikValues>({
  children,
  warningJsonSchema,
  validJsonSchema,
  ...props
}: FormikConfig<Values> & {
  children?: ((props: FormikProps<Values>) => ReactNode) | ReactNode
  warningJsonSchema?: JSONSchema7WithErrors
  validJsonSchema?: JSONSchema7WithErrors
}) {
  const errorsJsonSchemaValidator = useMemo(() => {
    if (!validJsonSchema) return undefined

    const validator = jsonSchemaFormikValidator(validJsonSchema)

    return (values: Values) => {
      const { valid, errors } = validator(values)

      return valid ? {} : errors
    }
  }, [validJsonSchema])

  const formik = useFormikWithWarnings({
    ...props,
    validationSchema: errorsJsonSchemaValidator ? undefined : props.validationSchema,
    validate: errorsJsonSchemaValidator || props.validate,
  })

  const warningsJsonSchemaValidator = useMemo(
    () => (warningJsonSchema ? jsonSchemaFormikValidator(warningJsonSchema) : undefined),

    [warningJsonSchema]
  )

  const previousIsValidating = usePreviousValue(formik.isValidating)

  useEffect(() => {
    // After validation has run, update warnings.
    if (!formik.isValidating && previousIsValidating) {
      if (warningsJsonSchemaValidator) {
        const warnings = warningsJsonSchemaValidator(formik.values)

        formik.setWarnings(warnings.errors)
      }
    }
  }, [previousIsValidating, formik.isValidating, formik.values, warningsJsonSchemaValidator])

  return (
    <FormikProvider value={formik}>
      {typeof children === 'function' ? children(formik) : children}
    </FormikProvider>
  )
}
